import React, { useState, useEffect } from "react";
import { getCommunity, getTopics, toggleLike, deleteTopic, getPostsByTopic, getPosts, deletePost, getComments, deleteComment, createComment} from "../api/community.js";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from '../utils/AuthContext';
import { useParams } from "react-router-dom";
import Loading from '../components/loading/Loading';
import TopicForm from "../components/TopicForm.jsx";
import PostForm from "../components/PostForm.jsx";
import CommentForm from "../components/CommentForm.jsx";
import Modal from "../components/Modal"; 
import NavbarLog from '../components/NavbarLog.jsx';
import Footer from '../components/Footer';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import jalaliday from "jalaliday";
import "dayjs/locale/fa";
import "../css/Community.css";

function CommunityPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const { lang } = useParams();
  const [setlang, setLang] = useState("fa")
  const [consoleError, setConsoleError] = useState("");
  const [community, setCommunity] = useState(null);
  const [comments, setComments] = useState([])
  const [topics, setTopics] = useState([]);
  const [posts, setPosts] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [showCommentAction, setComActDropdown] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  // For modals
  const [showCommentCont, setShowCommentCont] = useState(null);
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  
  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate(`/${lang}/login`, { state: { from: location }, replace: true });
      }
    }
  }, [user, loading, navigate, location]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const comm = await getCommunity(id);
        setCommunity(comm);

        const t = await getTopics(id);
        setTopics(t);
        if (t.length > 0) setSelectedTopic(t[0]);

      } catch (err) {
        setConsoleError("Error loading community:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    const fetchPosts = async () => {
      if (!selectedTopic) return;
      try {
        setLoading(true);
        const p = await getPosts(id, selectedTopic.id);
        setPosts(p);
        if (p.length > 0) setSelectedPost(p[0]);
      } catch (err) {
        setConsoleError("Error loading posts:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [id, selectedTopic]);

  useEffect(() => {
    if (selectedTopic) {
      const loadPosts = async () => {
        try {
          const postData = await getPostsByTopic(id, selectedTopic.id);
          setPosts(postData);
        } catch (err) {
          setConsoleError("Error loading posts:", err);
        }
      };
      loadPosts();
    }
  }, [selectedTopic, id]);

  useEffect(() => {
    if (!selectedTopic || posts.length === 0) return;
    const loadAllComments = async () => {
      try {
        const hasComments = posts.some(p => Array.isArray(p.comments) && p.comments.length > 0);
        if (hasComments) return;
        const postsWithComments = await Promise.all(
          posts.map(async (post) => {
            try {
              const comments = await getComments(id, selectedTopic.id, post.id);
              return { ...post, comments };
            } catch (err) {
              setConsoleError(`Error loading comments for post ${post.id}:`, err);
              return { ...post, comments: [] };
            }
          })
        );
        setPosts(postsWithComments);
        setCommentsLoaded(true);
      } catch (err) {
        setConsoleError("Error loading comments for all posts:", err);
      }
    };
  
    loadAllComments();
  }, [id, selectedTopic?.id, commentsLoaded, posts.length]);  

  const handleCommentSubmit = async (postId) => {
    if (!newComment.trim()) return;
    try {
      await createComment(id, selectedTopic.id, postId, newComment, file, {
        content: newComment,
        file: file,
      });
      setFile(null);
      setCommentsLoaded(false);
      setNewComment(" ");
      if (selectedTopic) {
        const loadPosts = async () => {
          try {
            const postData = await getPostsByTopic(id, selectedTopic.id);
            setPosts(postData);
          } catch (err) {
            setConsoleError("Error loading posts:", err);
          }
        };
        loadPosts();
      }

    } catch (err) {
      setConsoleError("Error adding comment:", err);
    }
  };
  
  const handleDeleteTopic = async (topicId) => {
    try {
      await deleteTopic(id, topicId);
      setTopics(topics.filter((t) => t.id !== topicId));
    } catch (err) {
      setConsoleError("Error deleting topic:", err);
    }
  };
  
  const handleDeletePost = async (postId) => {
    if (!window.confirm (setlang === "en" ? "Delete post?" : "پست رو حذف می کنی؟")) return ;
    try {
      await deletePost(id, selectedTopic.id, postId);
      setPosts(posts.filter((p) => p.id !== postId));
    } catch (err) {
      setConsoleError("Error deleting post:", err);
    }
  };
  
  const handleDeleteComment = async (postId, commentId) => {
    if (!window.confirm (setlang === "en" ? "Delete comment?" : "کامنت رو حذف می کنی؟")) return ;
    try {
      await deleteComment(id, selectedTopic.id, postId, commentId);
      const refreshed = await getPostsByTopic(id, selectedTopic.id);
      setPosts(refreshed);
      setCommentsLoaded(false);
    } catch (err) {
      setConsoleError("Error deleting comment:", err);
    }
  };

  const handleLike = async (postId) => {
    try {
      const res = await toggleLike(community.id, selectedTopic.id, postId);
      setPosts((prevPosts) =>
        prevPosts.map((p) =>
          p.id === postId ? { ...p, like_count: res.likes, is_liked: res.message === "Liked" } : p
        )
      );
    } catch (err) {
      setConsoleError("Error liking post:", err);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target !== e.currentTarget) {
      setComActDropdown(null);
    }
  };

  // Transation
  dayjs.extend(utc);
  dayjs.extend(jalaliday);
  const toPersiDigits = (str) =>
    str.replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

  useEffect (() => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if(lang !== "fa" && lang !== "en") {
      if ( userTimezone === "Asia/Tehran") {
        setLang(`fa`);
      } else {
        setLang(`en`);
      }
    } else {
      setLang(lang)
    }
  }, [lang])

  if (loading) return <Loading />;  
  if (!community) return <div className="p-6 text-red-600">{setlang==="en"?"Community not found.":"صفحه گفتگو یافت نشد"}</div>;

  return (
    <>
      <NavbarLog />
      <div className="community-page" onClick={(e) => handleOverlayClick(e)}>
        <div className="post-home">
          <div>
            <header
              className="community-header"
              style={{
                backgroundImage: `url(${community.image_url || ""})`,
              }}
              >
              <div className="overlay">
                <h1>{community.name}</h1>
                <p>{community.description}</p>
              </div>
            </header>
          </div>
          
          {/* #Create Topic#
          <div className="topics-bar">
            <div className="topics-tabs">
              {topics.map((topic) => (
                <div key={topic.id} className="topic-tab">
                  <button
                    className={`tab-btn ${selectedTopic?.id === topic.id ? "active" : ""}`}
                    onClick={() => setSelectedTopic(topic)}
                    >
                    {topic.title}
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteTopic(topic.id)}
                    >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <button className="add-topic-btn" onClick={() => setShowTopicModal(true)}>
              ＋ Add Topic
            </button>
          </div>
          */}

          {selectedTopic && (
            <div className="topic-section">
              <h2>{selectedTopic.title}</h2>
              <p>{selectedTopic.description}</p>

              {selectedTopic.image_url && (
                <img
                src={selectedTopic.image_url}
                alt={selectedTopic.title}
                className="topic-image"
                />
              )}
              
              <section className="posts-section">
                <button className="add-post-btn" onClick={() => { setEditingPost(null); setShowPostModal(true); }}>
                  {setlang === "en" ? "＋ New Post":"پست جدید ＋"}
                </button>

                {posts.length === 0 ? (
                  <p className="no-posts">{setlang === "en" ? "No posts yet" : "در این لحظه هیچ پستی وجود ندارد"}</p>
                ) : (
                  posts.map((post) => (
                    <div className="post-card" key={post.id}>
                      <h3>{post.author}</h3>
                      <p>{post.content}</p>
                      {post.image_url && (
                        <img
                        src={post.image_url}
                        alt="Post"
                        className="post-image"
                        />
                      )}

                      <div className="post-actions">
                        {post.edited && 
                        <p><small style={{fontStyle:"italic"}}>{setlang==="en" ? "edited on" : "ویرایش شده در"} {new Date(post.updated_at).toLocaleDateString()}</small></p>
                        }
                      </div>
                      
                      {user.id === post.author_id && 
                        <div className="post-actions">
                          <button onClick={() => { setEditingPost(post); setShowPostModal(true); }}>{setlang==="en"? "✏️ Edit":"ویرایش ✏️"}</button>
                          <button onClick={() => handleDeletePost(post.id)}>{setlang==="en"? "🗑️ Delete":"حذف 🗑️"}</button>
                        </div>
                      }

                      {setlang === "en" ? (
                      <div key={post.id} className="comment-section">
                        <textarea
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Write a comment ..."
                          rows="2"
                          />

                        {/*<input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setFile(e.target.files[0])}
                          />*/}

                        <button onClick={() => { setSelectedPost(post); handleCommentSubmit(post.id) }}>
                          Send
                        </button>

                      </div>
                      ):(
                      <div key={post.id} className="comment-section">
                        <textarea
                          style = {{textAlign:"right"}}
                          onChange = {(e) => setNewComment(e.target.value)}
                          placeholder = "... کامنت شما"
                          rows = "2"
                          />
                        <button onClick={() => { setSelectedPost(post); handleCommentSubmit(post.id) }}>
                          ارسال
                        </button>
                      </div>
                      )}
                      <div className="post-meta">
                        <span>
                          <button onClick={() => handleLike(post.id)}>
                            {post.is_liked ? "❤️":"🩶"} {post.like_count}
                          </button>
                        </span>
                        <span style={{cursor:"pointer"}} onClick={()=> setShowCommentCont(showCommentCont === post.id ? null : post.id)}> 💬 {post.comments?.length || 0}</span>
                      </div>

                      {post.comments?.sort ((a,b) => new Date(b.created_at) - new Date(a.created_at))
                      .map((comment) => (
                        <>
                        {showCommentCont === post.id && (
                        <div className="comment-container">
                          <div className="comment" key={comment.id}>
                            <div>
                              {user.id === comment.author_id && 
                                <span className="comment-actions">
                                  <button  className="comment-dash-button" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setComActDropdown((showCommentAction === comment.id ? null : comment.id))}}
                                    >
                                  ⋮⋮
                                  </button>
                                </span>
                              }
                            </div>
                            <div className="comment-dropdown-actions" >
                              {showCommentAction === comment.id && (
                                <div className="comment-dropdown" >
                                  <button onClick={() => {setSelectedPost(post); setEditingComment(comment); setShowCommentModal(true)}} title={setlang==="en" ? "edit" : "ویرایش"} >✏️</button>
                                  <button onClick={() => handleDeleteComment(post.id, comment.id)} title={setlang==="en" ? "delete" : "حذف"}>🗑️</button>
                                </div>
                              )}
                            </div>
                            <div style={{alignSelf:"top", textAlign:"right", maxWidth:"98%", minHeight:"fit-content", marginTop:"9px"}}>
                              {comment.content}
                            </div>

                            <div style={{display:"block", placeItems:"center"}}>
                              <span><small style={{display:"flex", textAlign:"right"}}>{comment.author}</small></span>
                              {comment.profile_image ? 
                              <img style= {{marginTop:"10px"}} className="comment-profile-pic" src={comment.profile_image} /> 
                              : 
                              null
                              }
                              {comment.edited ? 
                              <p><small style={{fontStyle:"italic"}}>{setlang==="en" ? "edited on " : "ویرایش شده در "}{dayjs.utc(comment.created_at).calendar("jalali").locale("fa").format("YYYY/MM/DD")}</small></p>
                              :
                              <p><small style={{fontStyle:"italic"}}>{dayjs.utc(comment.created_at).local().calendar("jalali").locale("fa").format("YYYY/MM/DD HH:mm")}</small></p>

                              }                            
                              </div>

                            {/*{comment.file_url && (
                              <img
                              src={comment.file_url}
                              alt="Comment"
                              className="comment-image"
                              />
                            )}*/}
                          </div>
                        </div>
                        )}
                        </>
                      ))}
                    </div>
                  ))
                )}
              </section>
            </div>
          )}
        </div>

        {/* Topic Modal */}
        {showTopicModal && (
          <Modal onClose={() => setShowTopicModal(false)}>
            <TopicForm
              communityId={id}
              onSuccess={async () => {
                const updatedTopics = await getTopics(id);
                setTopics(updatedTopics);
                setShowTopicModal(false);
              }}
            />
          </Modal>
        )}

        {/* Post Modal */}
        {showPostModal && (
          <Modal onClose={() => setShowPostModal(false)}>
            <PostForm
              communityId={id}
              topicId={selectedTopic.id}
              existingPost={editingPost}
              setlang = {setlang}
              onSuccess={async () => {
                const updatedPosts = await getPostsByTopic(id, selectedTopic.id);
                setPosts(updatedPosts);
                setShowPostModal(false);
              }}
            />
          </Modal>
        )}

        {/* Post Modal */}
        {showCommentModal && (
          <Modal onClose={() => setShowCommentModal(false)}>
            <CommentForm
              communityId={id}
              topicId={selectedTopic.id}
              postId={selectedPost.id}
              existingComment={editingComment}
              setlang = {setlang}
              onSuccess={async () => {
                const updatedPosts = await getPostsByTopic(id, selectedTopic.id);
                setPosts(updatedPosts);
                setShowCommentModal(false);
              }}
            />
          </Modal>
        )}
      </div>
      <Footer />
    </>
  );
}

export default CommunityPage;
